// Service pour gérer les matières

import { Matiere, MatiereCouleur, MatiereCouleurInput } from '../pages/Devis/ConfigurateurArticle/types';
import { apiService } from './ApiService';
import { entrepriseService } from './EntrepriseService';
import { FallbackAlerts } from './FallbackNotificationService';
import { apiFetch, buildAuthHeaders } from './httpClient';

/**
 * Classe pour gérer les matières
 */
class MatiereService {
  /**
   * Récupère le token d'authentification depuis le localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Récupère l'ID de l'utilisateur depuis le localStorage
   */
  private getUserId(): string | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.userId;
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'ID utilisateur:', error);
      }
    }
    return null;
  }

  /**
   * Crée les en-têtes pour les requêtes API avec authentification
   */
  private createHeaders(): HeadersInit {
    return buildAuthHeaders({ 'Content-Type': 'application/json' });
  }
  /**
   * Convertit un prix TTC en HT en fonction du taux de TVA
   * @param prixTTC Prix TTC à convertir
   * @param tauxTVA Taux de TVA (en pourcentage, ex: 20 pour 20%)
   * @returns Prix HT
   */
  private convertirTTCversHT(prixTTC: number, tauxTVA: number): number {
    return prixTTC / (1 + tauxTVA / 100);
  }
  
  /**
   * Ajuste les prix des matières en fonction du statut TVA de l'entreprise
   * @param matieres Liste des matières à ajuster
   * @param soumisATVA Indique si l'entreprise est soumise à TVA
   * @param tauxTVA Taux de TVA applicable
   * @returns Liste des matières avec les prix ajustés
   */
  private ajusterPrixMatieresSelonTVA(matieres: Matiere[], soumisATVA: boolean, tauxTVA: number): Matiere[] {
    if (!soumisATVA) {
      // Si l'entreprise n'est pas soumise à TVA, on garde les prix TTC
      return matieres;
    }
    
    // Si l'entreprise est soumise à TVA, on convertit les prix TTC en HT
    return matieres.map(matiere => ({
      ...matiere,
      prix: this.convertirTTCversHT(matiere.prix, tauxTVA)
    }));
  }

  /**
   * Normalise une couleur (string ou objet) vers {nom, hex}
   */
  private normalizeCouleur(couleur: MatiereCouleurInput): MatiereCouleur {
    if (!couleur && couleur !== '') {
      return { nom: '', hex: '' } as any;
    }
    if (typeof couleur === 'string') {
      return { nom: couleur, hex: '' } as any;
    }
    const nom = typeof (couleur as any).nom === 'string' ? (couleur as any).nom : '';
    const hex = typeof (couleur as any).hex === 'string' ? (couleur as any).hex : '';
    return { nom, hex } as any;
  }

  /**
   * Normalise une matière : minimumCommande défini et couleurs au format objet
   */
  private normalizeMatiere(matiere: Matiere): Matiere {
    const couleursNormalisees = (matiere.couleurs || []).map((c) => this.normalizeCouleur(c));
    const matiereNormalisee = {
      ...matiere,
      minimumCommande: matiere.minimumCommande || 1,
      // UI existante attend des strings, on expose les noms ici
      couleurs: couleursNormalisees.map((c) => c.nom),
    } as Matiere;
    // Stocker la version objet pour sauvegarde ultérieure
    (matiereNormalisee as any)._couleursNormalisees = couleursNormalisees;
    return matiereNormalisee;
  }

  /**
   * Récupère la liste des matières
   */
  async getMatieres(): Promise<Matiere[]> {
    try {
      // Récupérer les informations de l'entreprise pour déterminer le statut TVA
      const entreprise = await entrepriseService.getEntreprise() as any;
      const soumisATVA = entreprise.soumisATVA !== undefined ? entreprise.soumisATVA : !!entreprise.tva;
      const tauxTVA = entreprise.tauxTVA || 20; // Taux par défaut : 20%
      
      
      // Étape 1: Essayer de récupérer les matières depuis l'API
      try {
        const data = await apiFetch<any[]>(`/matieres`, {
          cache: 'no-store',
        });
        
        // S'assurer que toutes les matières ont minimumCommande défini
        const matieresAvecMinimum = data.map((matiere: Matiere) => this.normalizeMatiere(matiere));
        
        // Mettre à jour le localStorage
        localStorage.setItem('matieres', JSON.stringify(matieresAvecMinimum));
        
        // Ajuster les prix en fonction du statut TVA
        return this.ajusterPrixMatieresSelonTVA(matieresAvecMinimum, soumisATVA, tauxTVA);
      } catch (apiError) {
        console.warn("Erreur lors de la récupération des matières depuis l'API:", apiError);
        
        // 🚨 NOTIFICATION FALLBACK - Matières serveur indisponibles
        FallbackAlerts.matieres('Récupération matières depuis API backend');
        // Continuer avec le localStorage ou les données de démonstration
      }
      
      // Étape 2: Si l'API échoue, essayer de récupérer les matières depuis le localStorage
      const storedMatieres = localStorage.getItem('matieres');
      if (storedMatieres) {

        const matieres: Matiere[] = JSON.parse(storedMatieres);
        
        // S'assurer que toutes les matières ont minimumCommande défini
        const matieresAvecMinimum = matieres.map(matiere => this.normalizeMatiere(matiere));
        
        // Ajuster les prix en fonction du statut TVA
        return this.ajusterPrixMatieresSelonTVA(matieresAvecMinimum, soumisATVA, tauxTVA);
      }
      
      // Étape 3: Si le localStorage est vide, utiliser les données de démonstration

      const demoMatieres = this.getMatieresDemo().map(m => this.normalizeMatiere(m));
      
      // Stocker les données de démonstration dans le localStorage
      localStorage.setItem('matieres', JSON.stringify(demoMatieres));
      
      // Ajuster les prix en fonction du statut TVA
      return this.ajusterPrixMatieresSelonTVA(demoMatieres, soumisATVA, tauxTVA);
    } catch (error) {
      console.error('Erreur lors de la récupération des matières:', error);
      // Retourner des données de démonstration en cas d'erreur
      const demoMatieres = this.getMatieresDemo();
      
      try {
        // Récupérer les informations de l'entreprise pour déterminer le statut TVA
        const entreprise = await entrepriseService.getEntreprise() as any;
        const soumisATVA = entreprise.soumisATVA !== undefined ? entreprise.soumisATVA : !!entreprise.tva;
        const tauxTVA = entreprise.tauxTVA || 20; // Taux par défaut : 20%
        
        // Ajuster les prix en fonction du statut TVA
        return this.ajusterPrixMatieresSelonTVA(demoMatieres, soumisATVA, tauxTVA);
      } catch (e) {
        // En cas d'erreur, retourner les matières sans ajustement
        return demoMatieres;
      }
    }
  }

  /**
   * Récupère une matière par son ID
   */
  async getMatiere(id: string): Promise<Matiere> {
    try {
      // Récupérer les informations de l'entreprise pour déterminer le statut TVA
      const entreprise = await entrepriseService.getEntreprise() as any;
      const soumisATVA = entreprise.soumisATVA !== undefined ? entreprise.soumisATVA : !!entreprise.tva;
      const tauxTVA = entreprise.tauxTVA || 20; // Taux par défaut : 20%

      // Étape 1: Essayer de récupérer la matière depuis l'API
      try {
        const matiere = await apiFetch<any>(`/matieres/${id}`, {
          cache: 'no-store'
        });

        // S'assurer que minimumCommande est défini
        const matiereAvecMinimum = this.normalizeMatiere(matiere);
        
        // Mettre à jour le localStorage
        const storedMatieres = localStorage.getItem('matieres');
        if (storedMatieres) {
          const matieres: Matiere[] = JSON.parse(storedMatieres);
          const index = matieres.findIndex(m => m.id === id);
          if (index >= 0) {
            matieres[index] = matiereAvecMinimum;
          } else {
            matieres.push(matiereAvecMinimum);
          }
          localStorage.setItem('matieres', JSON.stringify(matieres));
        }
        
        // Ajuster le prix en fonction du statut TVA
        if (soumisATVA) {
          return {
            ...matiereAvecMinimum,
            prix: this.convertirTTCversHT(matiereAvecMinimum.prix, tauxTVA)
          };
        }
        
        return matiereAvecMinimum;
      } catch (apiError) {
        console.warn(`Erreur lors de la récupération de la matière ${id} depuis l'API:`, apiError);
        // Continuer avec le localStorage ou les données de démonstration
      }
      
      // Étape 2: Si l'API échoue, essayer de récupérer la matière depuis le localStorage
      const storedMatieres = localStorage.getItem('matieres');
      if (storedMatieres) {

        const matieres: Matiere[] = JSON.parse(storedMatieres);
        const matiere = matieres.find(m => m.id === id);
        if (matiere) {

          // S'assurer que minimumCommande est défini
          const matiereAvecMinimum = this.normalizeMatiere(matiere);
          
          // Ajuster le prix en fonction du statut TVA
          if (soumisATVA) {
            return {
              ...matiereAvecMinimum,
              prix: this.convertirTTCversHT(matiereAvecMinimum.prix, tauxTVA)
            };
          }
          
          return matiereAvecMinimum;
        }
      }
      
      // Étape 3: Si la matière n'est pas trouvée, retourner une matière de démonstration

      const matiereDemo = this.getMatiereDemo(id);
      
      // Ajuster le prix en fonction du statut TVA
      if (soumisATVA) {
        return {
          ...matiereDemo,
          prix: this.convertirTTCversHT(matiereDemo.prix, tauxTVA)
        };
      }
      
      return matiereDemo;
    } catch (error) {
      console.error('Erreur lors de la récupération de la matière:', error);
      // Retourner une matière de démonstration en cas d'erreur
      const matiereDemo = this.getMatiereDemo(id);
      
      try {
        // Récupérer les informations de l'entreprise pour déterminer le statut TVA
        const entreprise = await entrepriseService.getEntreprise() as any;
        const soumisATVA = entreprise.soumisATVA !== undefined ? entreprise.soumisATVA : !!entreprise.tva;
        const tauxTVA = entreprise.tauxTVA || 20; // Taux par défaut : 20%
        
        // Ajuster le prix en fonction du statut TVA
        if (soumisATVA) {
          return {
            ...matiereDemo,
            prix: this.convertirTTCversHT(matiereDemo.prix, tauxTVA)
          };
        }
      } catch (e) {
        // En cas d'erreur, ne pas ajuster le prix
      }
      
      return matiereDemo;
    }
  }

  /**
   * Sauvegarde une matière
   */
  async saveMatiere(matiere: Matiere): Promise<Matiere> {
    try {
      // Récupérer les matières existantes pour déterminer si c'est une mise à jour
      const storedMatieres = localStorage.getItem('matieres');
      let matieres: Matiere[] = storedMatieres ? JSON.parse(storedMatieres) : [];
      
      // Déterminer si c'est une mise à jour AVANT de modifier le localStorage
      const existingIndex = matiere.id ? matieres.findIndex(m => m.id === matiere.id) : -1;
      const isUpdate = existingIndex >= 0;
      
      // S'assurer que minimumCommande est défini
      if (matiere.minimumCommande === undefined) {
        matiere.minimumCommande = 1; // Valeur par défaut
      }
      
      // Normaliser la matière (minimumCommande + couleurs objets)
      const matiereNormalisee = this.normalizeMatiere(matiere);

      let savedMatiere: Matiere;
      
      // Étape 1: Sauvegarder dans le localStorage
      if (matiere.id) {
        // Mettre à jour une matière existante
        if (isUpdate) {
          matieres[existingIndex] = matiereNormalisee;
        } else {
          matieres.push(matiereNormalisee);
        }
        savedMatiere = matiereNormalisee;
      } else {
        // Ajouter une nouvelle matière
        const newMatiere = {
          ...matiereNormalisee,
          id: `matiere-${Date.now()}`
        };
        matieres.push(newMatiere);
        savedMatiere = newMatiere;
      }
      
      // Stocker les matières mises à jour dans le localStorage
      localStorage.setItem('matieres', JSON.stringify(matieres));
      
      // Étape 2: Synchroniser avec le backend
      try {
        const method = isUpdate ? 'PUT' : 'POST';
        const path = method === 'PUT' ? `/matieres/${savedMatiere.id}` : `/matieres`;
        const couleursPourSauvegarde =
          (savedMatiere as any)._couleursNormalisees ||
          (savedMatiere.couleurs || []).map((c) => this.normalizeCouleur(c));
        const payload = {
          ...savedMatiere,
          couleurs: couleursPourSauvegarde,
        };
        const updatedMatiere = await apiFetch<any>(path, {
          method,
          body: JSON.stringify(payload),
        });

        if (updatedMatiere && updatedMatiere.id) {
          const index = matieres.findIndex(m => m.id === savedMatiere.id);
          if (index >= 0) {
            matieres[index] = updatedMatiere;
          } else {
            matieres.push(updatedMatiere);
          }
          localStorage.setItem('matieres', JSON.stringify(matieres));
          return updatedMatiere;
        }
      } catch (syncError) {
        console.warn('Erreur lors de la synchronisation avec le backend:', syncError);
        
        // 🚨 NOTIFICATION FALLBACK - Synchronisation matières
        FallbackAlerts.sauvegarde('Synchronisation matières avec backend');
        // Ne pas échouer complètement, continuer avec la version locale
      }
      
      // Retourner la matière sauvegardée localement
      return savedMatiere;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la matière:', error);
      // Simuler la sauvegarde en cas d'erreur
      return {
        ...matiere,
        id: matiere.id || `matiere-${Date.now()}`,
        minimumCommande: matiere.minimumCommande || 1 // S'assurer que minimumCommande est défini
      };
    }
  }

  /**
   * Supprime une matière
   */
  async deleteMatiere(id: string): Promise<void> {
    try {
      // Étape 1: Supprimer du localStorage
      const storedMatieres = localStorage.getItem('matieres');
      if (!storedMatieres) return;
      
      let matieres: Matiere[] = JSON.parse(storedMatieres);
      
      // Supprimer la matière
      matieres = matieres.filter(m => m.id !== id);
      
      // Stocker les matières mises à jour dans le localStorage
      localStorage.setItem('matieres', JSON.stringify(matieres));
      
      // Étape 2: Synchroniser avec le backend
      try {
        await apiFetch(`/matieres/${id}`, { method: 'DELETE' });
      } catch (syncError) {
        console.warn('Erreur lors de la synchronisation avec le backend:', syncError);
        
        // 🚨 NOTIFICATION FALLBACK - Synchronisation matières
        FallbackAlerts.sauvegarde('Synchronisation matières avec backend');
        // Ne pas échouer complètement, la suppression locale a déjà été effectuée
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la matière:', error);
      // Ne rien faire en cas d'erreur
    }
  }

  /**
   * Retourne des matières de démonstration
   */
  private getMatieresDemo(): Matiere[] {
    return [
      {
        id: '1',
        nom: 'Similicuir Premium',
        type: 'similicuir',
        laize: 140,
        prix: 24.90,
        minimumCommande: 1, // Ajouter cette propriété
        couleurs: ['Blanc', 'Noir', 'Beige', 'Gris', 'Bleu marine'],
      },
      {
        id: '2',
        nom: 'TEP Marine',
        type: 'tep',
        laize: 150,
        prix: 32.50,
        minimumCommande: 1, // Ajouter cette propriété
        couleurs: ['Blanc', 'Bleu marine', 'Gris clair'],
      },
      {
        id: '3',
        nom: 'Mesh Outdoor',
        type: 'mesh',
        laize: 180,
        prix: 18.75,
        minimumCommande: 1, // Ajouter cette propriété
        couleurs: ['Noir', 'Gris', 'Beige'],
      },
      {
        id: '4',
        nom: 'Bâche PVC',
        type: 'bache',
        laize: 200,
        prix: 15.90,
        minimumCommande: 1, // Ajouter cette propriété
        couleurs: ['Blanc', 'Gris', 'Beige', 'Vert'],
      },
    ];
  }

  /**
   * Retourne une matière de démonstration
   */
  private getMatiereDemo(id: string): Matiere {
    const matieres = this.getMatieresDemo();
    const matiere = matieres.find(m => m.id === id);
    
    if (matiere) {
      return matiere;
    }
    
    return {
      id,
      nom: 'Matière inconnue',
      type: 'autre',
      laize: 150,
      prix: 20.00,
      couleurs: ['Blanc', 'Noir'],
    };
  }
}

// Exporter une instance unique du service
export const matiereService = new MatiereService();
